"use client";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import TextareaAutosize from "react-textarea-autosize";
import {
   SendHorizontal,
   Loader2,
   MessageSquarePlus,
   AlertCircle,
   Mic,
   X,
   ShieldBan
} from "lucide-react";
import {
   Button,
   AiSourceSelector,
   Alert,
   AlertTitle,
   AlertDescription
} from "@/app/components/ui";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { cn } from "@/app/lib/utils";
import {
   ATTACHMENT_PROCESSING_STATUSES,
   ATTACHMENT_ONLY_PROMPT,
   isAllowedChatAttachmentFile,
   MAX_MESSAGES_PER_TOPIC,
   USER_INPUT_LIMITS_DEFAULTS
} from "@/app/lib/constants";
import { ChatInputAttachButton } from "./ChatInputAttachButton";
import { ChatInputAttachments } from "./ChatInputAttachments";
import { ChatInputVoice, ChatInputVoiceStatus } from "./ChatInputVoice";
import { useMediaQuery } from "@/app/hooks";
import { KEY } from "@/app/lib/enums";

type ChatInputProps = {
   loading: boolean;
   error: string | null;
   textareaRef: React.RefObject<HTMLTextAreaElement | null>;
   className?: string;
   isEmpty?: boolean;
};

export const ChatInput = (props: ChatInputProps) => {
   const { loading, error, textareaRef, className, isEmpty = false } = props;
   const translation = useTranslations("Home");
   const router = useRouter();
   const {
      historyItem,
      triggerSendMessageEvent,
      messagePageInfo,
      resetState,
      isTemporaryChat,
      maxUserInputLength,
      setErrorChat,
      pendingAttachments,
      addPendingAttachments,
      maxFilesPerMessage,
      chatAttachmentPolicy
   } = useStateHistoryChatPage();
   const [text, setText] = useState("");
   const [isDraggingFile, setIsDraggingFile] = useState(false);
   const [isVoiceRecording, setIsVoiceRecording] = useState(false);
   const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
   const voiceAnalyserRef = useRef<AnalyserNode | null>(null);
   const startRecordingRef = useRef<(() => void) | null>(null);
   const isMobile = useMediaQuery("(max-width: 768px)");

   const isUploading = pendingAttachments.some((item) =>
      ATTACHMENT_PROCESSING_STATUSES.includes(
         item.status as (typeof ATTACHMENT_PROCESSING_STATUSES)[number]
      )
   );
   // Only "ready" counts: a failed upload would keep the send button lit while
   // submitting silently did nothing.
   const hasSendableAttachment = pendingAttachments.some(
      (item) => item.status === "ready"
   );
   const canSend = Boolean(text.trim() || hasSendableAttachment) && !isUploading;
   const showVoiceShortcut = !text.trim() && !pendingAttachments.length;
   const isVoiceBusy = isVoiceRecording || isVoiceProcessing;

   const handleVoiceTranscript = useCallback((transcript: string) => {
      setText(transcript);
   }, []);

   const stageFiles = useCallback(
      (files: FileList | null) => {
         const selected = Array.from(files || []);
         const accepted = selected.filter((file) =>
            isAllowedChatAttachmentFile(file, chatAttachmentPolicy)
         );
         if (accepted.length !== selected.length) {
            setErrorChat(translation("unsupportedAttachmentType"));
         }
         const remaining = Math.max(0, maxFilesPerMessage - pendingAttachments.length);
         if (accepted.length > remaining) {
            setErrorChat(
               translation("tooManyAttachments", { maxFiles: maxFilesPerMessage })
            );
         }
         if (accepted.length) addPendingAttachments(accepted);
      },
      [
         addPendingAttachments,
         chatAttachmentPolicy,
         maxFilesPerMessage,
         pendingAttachments.length,
         setErrorChat,
         translation
      ]
   );

   const submit = useCallback(
      (
         event:
            | React.KeyboardEvent<HTMLTextAreaElement>
            | React.MouseEvent<HTMLButtonElement>
      ) => {
         const typedMessage = textareaRef.current?.value.trim() || "";
         const message = typedMessage || ATTACHMENT_ONLY_PROMPT;
         triggerSendMessageEvent(event, message);
         setText("");
      },
      [triggerSendMessageEvent, textareaRef]
   );

   useEffect(() => {
      textareaRef.current?.focus();
   }, [historyItem?.id]);

   const checkMessageLimit = useCallback(() => {
      return (messagePageInfo?.total || 0) >= MAX_MESSAGES_PER_TOPIC;
   }, [messagePageInfo?.total]);

   const handleCreateNewChatFromLimit = useCallback(() => {
      resetState();
      router.push("/");
   }, [resetState, router]);

   // Intelligent parser to translate structured and legacy errors
   const parseErrorMessage = useCallback(
      (errorStr: string | null) => {
         if (!errorStr) return null;
         try {
            const parsed = JSON.parse(errorStr);
            const code = parsed.code || "UNKNOWN_ERROR";
            const message = parsed.message || translation("unknownError");

            switch (code) {
               case "USAGE_CHECK_FAILED":
               case "INTERNAL_ERROR":
                  return {
                     title: translation("usageVerificationFailedTitle"),
                     description: translation("usageVerificationFailedDesc")
                  };
               case "RATE_LIMIT_EXCEEDED":
                  return {
                     title: translation("quotaExceededTitle"),
                     description: translation("quotaExceededDesc")
                  };
               case "CONCURRENT_LIMIT_EXCEEDED":
                  return {
                     title: translation("concurrentLimitExceededTitle"),
                     description: translation("concurrentLimitExceededDesc")
                  };
               case "DAILY_LIMIT_EXCEEDED":
                  return {
                     title: translation("dailyLimitExceededTitle"),
                     description: translation("dailyLimitExceededDesc")
                  };
               case "ABUSE_BLOCKED":
                  return {
                     title: translation("abuseBlockedTitle"),
                     description: translation("abuseBlockedDesc")
                  };
               case "ACCOUNT_BANNED":
                  return {
                     title: translation("accountBannedTitle"),
                     description: translation("accountBannedDesc")
                  };
               case "AUTHENTICATION_REQUIRED":
                  return {
                     title: translation("authRequiredTitle"),
                     description: translation("authRequiredDesc")
                  };
               case "ACCESS_DENIED":
                  return {
                     title: translation("accessDeniedTitle"),
                     description: translation("accessDeniedDesc")
                  };
               case "SERVER_NOT_FOUND":
                  return {
                     title: translation("connectionErrorTitle"),
                     description: translation("serverNotFound")
                  };
               default:
                  return {
                     title: translation("errorOccurred"),
                     description: message
                  };
            }
         } catch (e) {
            // Fallback legacy parser
            const errCodeMatch = errorStr.match(/\(ErrorCode:\s*(\d+)\)/i);
            if (errCodeMatch) {
               const statusCode = errCodeMatch[1];
               if (statusCode === "403") {
                  return {
                     title: translation("accessDeniedTitle"),
                     description: translation("accessDeniedDesc")
                  };
               }
               if (statusCode === "429") {
                  return {
                     title: translation("quotaExceededTitle"),
                     description: translation("quotaExceededDesc")
                  };
               }
            }
            return {
               title: translation("errorOccurred"),
               description: errorStr
            };
         }
      },
      [translation]
   );

   const errorDetails = parseErrorMessage(error);

   // Detect if the user is permanently banned — disables all input
   const isBanned = (() => {
      try {
         const parsed = error ? JSON.parse(error) : null;
         return parsed?.code === "ACCOUNT_BANNED";
      } catch {
         return false;
      }
   })();

   if (checkMessageLimit()) {
      return (
         <div className={className}>
            <div className="max-w-3xl mx-auto">
               <div className="flex flex-col items-center justify-center py-8 text-center space-y-4 bg-gray-50 dark:bg-gray-800/50 rounded-3xl border border-gray-200 dark:border-gray-700">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-3">
                     <MessageSquarePlus className="h-8 w-8 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="space-y-2">
                     <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {translation("maxMessageLimitReached") ||
                           "Maximum Message Limit Reached"}
                     </h3>
                     <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
                        {translation("maxMessageLimitDescription")}
                     </p>
                  </div>
                  <Button
                     onClick={handleCreateNewChatFromLimit}
                     size="default"
                     className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                     <MessageSquarePlus className="h-4 w-4 mr-2" />
                     {translation("createNewChat") || "Create New Chat"}
                  </Button>
               </div>
            </div>
         </div>
      );
   }

   return (
      <div className={className}>
         {errorDetails && (
            <div className="max-w-3xl mx-auto mb-4 px-4 animate-in fade-in slide-in-from-top-2 duration-300">
               <Alert
                  variant="destructive"
                  className="relative pr-10 shadow-lg border border-red-500/20 bg-gradient-to-r from-rose-500/10 via-red-500/5 to-transparent backdrop-blur-md rounded-2xl p-4 flex items-start space-x-3 group"
               >
                  {/* Pulsing indicator */}
                  <div className="bg-rose-500/10 p-2 rounded-xl text-rose-500 animate-pulse flex-shrink-0">
                     {isBanned ? <ShieldBan size={20} /> : <AlertCircle size={20} />}
                  </div>

                  {/* Title & Desc */}
                  <div className="flex-1 space-y-1">
                     <AlertTitle className="text-sm font-semibold text-rose-600 dark:text-rose-400">
                        {errorDetails.title}
                     </AlertTitle>
                     <AlertDescription className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                        {errorDetails.description}
                     </AlertDescription>
                  </div>

                  {/* Close Button */}
                  <Button
                     variant="ghost"
                     size="icon"
                     onClick={() => setErrorChat(null)}
                     className="h-8 w-8 rounded-lg hover:bg-rose-500/10 text-gray-400 hover:text-rose-600 dark:text-gray-500 dark:hover:text-rose-400 hover:rotate-90 transition-all duration-300 flex-shrink-0"
                     aria-label={translation("close") || "Close"}
                  >
                     <X size={16} />
                  </Button>
               </Alert>
            </div>
         )}

         <div
            onDragOver={(e) => {
               e.preventDefault();
               if (!isBanned) setIsDraggingFile(true);
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={(e) => {
               e.preventDefault();
               setIsDraggingFile(false);
               if (!isBanned) stageFiles(e.dataTransfer.files);
            }}
            className={cn(
               "max-w-3xl mx-auto flex flex-col bg-white dark:bg-gray-800 rounded-3xl relative border border-gray-200 dark:border-gray-700",
               isEmpty &&
                  "shadow-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800",
               isTemporaryChat && "border-dashed border-2 border-slate-400",
               isDraggingFile && "border-primary/60 bg-primary/5 dark:bg-primary/10"
            )}
         >
            <ChatInputAttachments isSending={loading} />

            {isVoiceBusy ? (
               <ChatInputVoiceStatus
                  analyser={voiceAnalyserRef.current}
                  isProcessing={isVoiceProcessing}
               />
            ) : (
               <div className="relative flex-1">
                  <TextareaAutosize
                     value={text}
                     onChange={(e) => setText(e.target.value)}
                     autoFocus
                     ref={textareaRef}
                     disabled={isBanned}
                     onKeyDown={(e) => {
                        if (
                           e.key === KEY.Enter &&
                           !e.shiftKey &&
                           !isMobile &&
                           !loading &&
                           canSend
                        ) {
                           e.preventDefault();
                           submit(e);
                        }
                     }}
                     onPaste={(e) => {
                        if (e.clipboardData.files.length) {
                           e.preventDefault();
                           stageFiles(e.clipboardData.files);
                        }
                     }}
                     placeholder={
                        isTemporaryChat
                           ? translation("enterQuestionTemporary")
                           : translation("enterQuestion")
                     }
                     minRows={1}
                     maxRows={10}
                     maxLength={
                        maxUserInputLength ||
                        USER_INPUT_LIMITS_DEFAULTS.MAX_USER_INPUT_LENGTH
                     }
                     className={cn(
                        "w-full pb-2 pt-4 px-4 rounded-t-3xl border-0 outline-none focus:outline-none ring-0 focus:ring-0 focus:shadow-none resize-none dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500",
                        isEmpty && "transition-all duration-200",
                        isTemporaryChat && "bg-transparent dark:bg-transparent"
                     )}
                  />
                  <div className="absolute bottom-1 right-3 text-[10px] text-gray-400 dark:text-gray-500 pointer-events-none">
                     {text.length > 0 &&
                        `${text.length.toLocaleString()} / ${(maxUserInputLength || USER_INPUT_LIMITS_DEFAULTS.MAX_USER_INPUT_LENGTH).toLocaleString()}`}
                  </div>
                  <div
                     className="pointer-events-none absolute bottom-0 left-0 right-0 h-4 from-gray-50 dark:from-gray-800 to-transparent rounded-b-none"
                     aria-hidden="true"
                  />
               </div>
            )}

            {/* Action buttons area - fixed at bottom */}
            <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-1">
               <div className="flex min-w-0 flex-1 items-center gap-2">
                  <ChatInputAttachButton
                     disabled={loading || isBanned || isVoiceBusy}
                     chatAttachmentPolicy={chatAttachmentPolicy}
                     onFilesSelected={stageFiles}
                  />
                  <AiSourceSelector
                     hiddenModel
                     disabled={isVoiceBusy}
                     className={cn(
                        "h-8 w-40 shrink-0 rounded-xl border-[1px] text-xs sm:w-auto"
                     )}
                  />
               </div>
               <ChatInputVoice
                  startRef={startRecordingRef}
                  onRecordingChange={setIsVoiceRecording}
                  onProcessingChange={setIsVoiceProcessing}
                  onTranscript={handleVoiceTranscript}
                  analyserRef={voiceAnalyserRef}
               />
               {!isVoiceRecording && isVoiceProcessing ? (
                  <Button
                     disabled
                     aria-label={translation("voiceModeProcessing")}
                     title={translation("voiceModeProcessing")}
                     className="m-0 h-10 w-10 shrink-0 justify-center rounded-full p-2.5"
                  >
                     <SendHorizontal
                        size={4}
                        className="-rotate-90 text-primary-foreground"
                     />
                  </Button>
               ) : !isVoiceRecording && showVoiceShortcut ? (
                  <Button
                     onClick={() => startRecordingRef.current?.()}
                     disabled={loading || isBanned}
                     aria-label={translation("recordVoice")}
                     title={translation("recordVoice")}
                     className="m-0 h-10 w-10 shrink-0 justify-center rounded-full p-2.5"
                  >
                     <Mic size={18} className="text-primary-foreground" />
                  </Button>
               ) : !isVoiceRecording ? (
                  <Button
                     onClick={submit}
                     disabled={loading || !canSend || isBanned}
                     className="rounded-full h-10 w-10 justify-center p-2.5 m-0 shrink-0"
                  >
                     {loading ? (
                        <Loader2 className="animate-spin" size={4} />
                     ) : (
                        <SendHorizontal
                           size={4}
                           className="-rotate-90 text-primary-foreground"
                        />
                     )}
                  </Button>
               ) : null}
            </div>
         </div>
      </div>
   );
};
